import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Plus, ArrowRight, Trash2, ShieldCheck } from 'lucide-react';
import type { CompanyProfile, PicContact, UpsertCompanyInput } from '@klinik/shared';
import { PRIVACY_POLICY_VERSION } from '@klinik/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge, type BadgeTone } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Logo } from '../components/Logo';
import { fadeOnly, fadeRise } from '../lib/motion';

const STATUS_LABEL: Record<PicContact['status'], { label: string; tone: BadgeTone }> = {
  pending: { label: 'Belum', tone: 'draft' },
  invited: { label: 'Diundang', tone: 'progress' },
  in_progress: { label: 'Mengisi', tone: 'progress' },
  done: { label: 'Selesai', tone: 'done' },
};

const emptyPic = { name: '', division: '', email: '', whatsapp: '' };

export function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const variants = reduce ? fadeOnly : fadeRise;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(user?.companyId ?? null);

  const [form, setForm] = useState({
    name: '',
    industry: '',
    employeeCount: '',
    ownerName: user?.name ?? '',
    ownerWhatsapp: '',
  });
  const [pics, setPics] = useState<PicContact[]>([]);
  const [newPic, setNewPic] = useState(emptyPic);
  // UU PDP: explicit privacy-policy consent (required to create the company).
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    void (async () => {
      const res = await api.get<{ company: CompanyProfile | null }>('/companies/me');
      if (res.ok && res.data.company) {
        const c = res.data.company;
        setCompanyId(c.id);
        setForm({
          name: c.name,
          industry: c.industry ?? '',
          employeeCount: c.employeeCount != null ? String(c.employeeCount) : '',
          ownerName: c.ownerName ?? user?.name ?? '',
          ownerWhatsapp: c.ownerWhatsapp ?? '',
        });
        const picsRes = await api.get<{ company: CompanyProfile; pics: PicContact[] }>(
          `/companies/${c.id}`,
        );
        if (picsRes.ok) setPics(picsRes.data.pics);
      }
      setLoading(false);
    })();
  }, [user?.name]);

  function payload(): UpsertCompanyInput {
    return {
      name: form.name.trim(),
      industry: form.industry.trim() || null,
      employeeCount: form.employeeCount ? Number(form.employeeCount) : null,
      ownerName: form.ownerName.trim() || null,
      ownerWhatsapp: form.ownerWhatsapp.trim() || null,
      // Sent only on first create (the API requires it; PATCH ignores it).
      acceptPolicyVersion: accepted ? PRIVACY_POLICY_VERSION : undefined,
    };
  }

  async function saveCompany(): Promise<string | null> {
    if (!form.name.trim()) {
      setError('Nama perusahaan wajib diisi.');
      return null;
    }
    if (!companyId && !accepted) {
      setError('Mohon setujui Kebijakan Privasi (UU PDP) untuk melanjutkan.');
      return null;
    }
    setError(null);
    if (!companyId) {
      const res = await api.post<{ company: CompanyProfile }>('/companies', payload());
      if (!res.ok) {
        setError(res.error.message);
        return null;
      }
      setCompanyId(res.data.company.id);
      await refresh(); // session companyId now set → routing won't bounce
      return res.data.company.id;
    }
    const res = await api.patch<{ company: CompanyProfile }>(`/companies/${companyId}`, payload());
    if (!res.ok) {
      setError(res.error.message);
      return null;
    }
    return companyId;
  }

  async function addPic() {
    if (!newPic.name.trim() || !newPic.division.trim()) {
      setError('Nama & divisi PIC wajib diisi.');
      return;
    }
    setSaving(true);
    const id = companyId ?? (await saveCompany());
    if (!id) {
      setSaving(false);
      return;
    }
    const res = await api.post<{ pic: PicContact }>(`/companies/${id}/pics`, {
      name: newPic.name.trim(),
      division: newPic.division.trim(),
      email: newPic.email.trim() || null,
      whatsapp: newPic.whatsapp.trim() || null,
    });
    if (res.ok) {
      setPics((p) => [...p, res.data.pic]);
      setNewPic(emptyPic);
    } else {
      setError(res.error.message);
    }
    setSaving(false);
  }

  async function removePic(id: string) {
    if (!companyId) return;
    const res = await api.del(`/companies/${companyId}/pics/${id}`);
    if (res.ok) setPics((p) => p.filter((x) => x.id !== id));
  }

  async function saveAndContinue() {
    setSaving(true);
    const id = await saveCompany();
    setSaving(false);
    if (id) navigate('/');
  }

  if (loading) {
    return (
      <div className="h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-[18px]">
      <div className="mx-auto max-w-6xl">
        <div className="mb-5 flex items-center justify-between">
          <Logo />
          <div className="text-sm text-slate-500">Onboarding · Profil Perusahaan &amp; Tim</div>
        </div>

        <motion.div initial="hidden" animate="show" variants={variants}>
          <div className="mb-4 flex items-center justify-between">
            <h1 className="font-display text-2xl font-bold text-slate-900">
              Profil Perusahaan &amp; Tim
            </h1>
            <Button onClick={saveAndContinue} disabled={saving} icon={<ArrowRight size={15} />}>
              Simpan &amp; Lanjut
            </Button>
          </div>

          {error && (
            <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}

          {/* UU PDP consent — required to create the company (first onboarding). */}
          {!companyId && (
            <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-3.5 text-xs text-blue-900">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
                aria-label="Setujui Kebijakan Privasi"
              />
              <span>
                Saya, sebagai Owner, menyetujui <b>Kebijakan Privasi (UU PDP)</b> Klinik Bisnis —
                termasuk pemrosesan dokumen &amp; data perusahaan oleh layanan AI dan{' '}
                <b>transfer ke sub-prosesor di luar negeri</b> (Cloudflare, Anthropic/Claude, Neon,
                Resend, Google) sebagaimana diuraikan di kebijakan tersebut.
                <span className="ml-1 text-blue-500">(v{PRIVACY_POLICY_VERSION})</span>
              </span>
            </label>
          )}

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Identity */}
            <Card className="lg:col-span-1">
              <div className="mb-3 text-sm font-semibold text-slate-800">Identitas Perusahaan</div>
              <Field label="Nama Perusahaan" required>
                <input
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="PT Maju Bersama"
                />
              </Field>
              <Field label="Industri">
                <input
                  className={inputCls}
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  placeholder="Manufaktur"
                />
              </Field>
              <Field label="Jumlah Karyawan">
                <input
                  className={inputCls}
                  type="number"
                  min={0}
                  value={form.employeeCount}
                  onChange={(e) => setForm({ ...form, employeeCount: e.target.value })}
                  placeholder="120"
                />
              </Field>
              <Field label="Owner">
                <input
                  className={inputCls}
                  value={form.ownerName}
                  onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                />
              </Field>
              <Field label="Email (akun)">
                <input className={`${inputCls} bg-slate-100 text-slate-500`} value={user?.email ?? ''} readOnly />
              </Field>
              <Field label="WhatsApp">
                <input
                  className={inputCls}
                  value={form.ownerWhatsapp}
                  onChange={(e) => setForm({ ...form, ownerWhatsapp: e.target.value })}
                  placeholder="+62 812-3456-789"
                />
              </Field>
            </Card>

            {/* PIC mapping */}
            <Card className="lg:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">
                  PIC per Divisi{' '}
                  <span className="font-normal text-slate-400">(menerima kuesioner via tautan)</span>
                </div>
              </div>

              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                    {['PIC', 'Divisi', 'Kontak', 'Status', ''].map((h) => (
                      <th key={h} className="pb-2 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pics.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 text-sm">
                      <td className="py-3 pr-3 font-medium text-slate-800">{p.name}</td>
                      <td className="py-3 pr-3 text-slate-600">{p.division}</td>
                      <td className="py-3 pr-3 text-slate-500">{p.email ?? p.whatsapp ?? '—'}</td>
                      <td className="py-3 pr-3">
                        <Badge tone={STATUS_LABEL[p.status].tone}>{STATUS_LABEL[p.status].label}</Badge>
                      </td>
                      <td className="py-3">
                        <button
                          onClick={() => void removePic(p.id)}
                          className="text-slate-300 hover:text-red-500"
                          aria-label={`Hapus PIC ${p.name}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {pics.length === 0 && (
                    <tr className="border-t border-slate-100">
                      <td colSpan={5} className="py-6 text-center text-sm text-slate-400">
                        Belum ada PIC. Tambahkan PIC per divisi di bawah.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Add PIC row */}
              <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl border border-dashed border-slate-200 p-3 md:grid-cols-5">
                <input
                  className={inputCls}
                  placeholder="Nama PIC"
                  value={newPic.name}
                  onChange={(e) => setNewPic({ ...newPic, name: e.target.value })}
                />
                <input
                  className={inputCls}
                  placeholder="Divisi"
                  value={newPic.division}
                  onChange={(e) => setNewPic({ ...newPic, division: e.target.value })}
                />
                <input
                  className={inputCls}
                  placeholder="Email"
                  value={newPic.email}
                  onChange={(e) => setNewPic({ ...newPic, email: e.target.value })}
                />
                <input
                  className={inputCls}
                  placeholder="WhatsApp"
                  value={newPic.whatsapp}
                  onChange={(e) => setNewPic({ ...newPic, whatsapp: e.target.value })}
                />
                <Button variant="ghost" onClick={() => void addPic()} disabled={saving} icon={<Plus size={15} />}>
                  Tambah
                </Button>
              </div>

              <div className="mt-4 flex gap-2 rounded-xl bg-blue-50 p-3 text-xs text-blue-800">
                <ShieldCheck size={16} className="mt-0.5 shrink-0" aria-hidden />
                <span>
                  Login via <b>Google OAuth</b>. Peran: <b>Owner</b> (klien) · <b>Coach</b> &amp;{' '}
                  <b>Admin</b> (Nine Co). PIC = magic-link bertoken, tanpa akun.
                </span>
              </div>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

const inputCls =
  'w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand';

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </div>
      {children}
    </div>
  );
}
